/**
 * AudioClock — the master timebase for spawn timing (Design Bible §5).
 *
 * For determinism (FS-02 R41), the beat is derived from accumulated simulation time, not from
 * AudioContext.currentTime. That makes muted and unmuted runs bit-identical (Pillar P6): the
 * synth (audio/synth.ts) merely *sonifies* this grid, it never drives gameplay.
 */
import { DEFAULT_BPM } from '../core/constants';

export class AudioClock {
  bpm: number;
  private simSeconds = 0;

  constructor(bpm: number = DEFAULT_BPM) {
    this.bpm = bpm;
  }

  /** Seconds since run start (sim-driven). */
  get time(): number {
    return this.simSeconds;
  }

  /** Beats per second. */
  get bps(): number {
    return this.bpm / 60;
  }

  /** Continuous beat position (float). */
  get beat(): number {
    return this.simSeconds * this.bps;
  }

  get bar(): number {
    return Math.floor(this.beat / 4);
  }

  /** Position within the current bar, 0..1. */
  get barPhase(): number {
    const b = this.beat / 4;
    return b - Math.floor(b);
  }

  /** Position within the current beat, 0..1. */
  get beatPhase(): number {
    const b = this.beat;
    return b - Math.floor(b);
  }

  /** Seconds until the next integer beat boundary. */
  timeToNextBeat(): number {
    const frac = this.beatPhase;
    return (1 - frac) / this.bps;
  }

  /** Absolute sim time of beat index n. */
  timeOfBeat(n: number): number {
    return n / this.bps;
  }

  advance(dt: number): void {
    this.simSeconds += dt;
  }

  reset(): void {
    this.simSeconds = 0;
  }
}
